import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import type { DashboardData } from '../store/types';
import styles from './cpSummaryCards.module.scss';

interface CardConfig {
  icon: string;
  label: string;
  value: string | number;
  subtitle: string;
  accent: string;
  accentLight: string;
}

const Card = ({ icon, label, value, subtitle, accent, accentLight }: CardConfig) => (
  <div
    className={styles.card}
    style={
      {
        '--card-accent': accent,
        '--card-accent-light': accentLight,
      } as React.CSSProperties
    }
  >
    <div className={styles.iconWrap}>
      <i className={icon} />
    </div>
    <div>
      <div className={styles.label}>{label}</div>
      <div className={styles.value}>{value}</div>
      <div className={styles.subtitle}>{subtitle}</div>
    </div>
  </div>
);

const formatMonthLabel = (m: string): string => {
  const [yr, mn] = m.split('-');
  const d = new Date(parseInt(yr), parseInt(mn) - 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const buildCards = (data: DashboardData, selectedMonth: string, months: string[]): CardConfig[] => {
  const gt = data.grand_total[selectedMonth];
  const totalJobs12 = Object.values(data.forecast_jobs).reduce((a, b) => a + b, 0);
  const currentMonthJobs = data.forecast_jobs[selectedMonth] ?? 0;

  // Peak headcount across all months
  let peakValue = 0;
  let peakMonth = months[0];
  for (const m of months) {
    const hc = data.grand_total[m]?.total_hc ?? 0;
    if (hc > peakValue) {
      peakValue = hc;
      peakMonth = m;
    }
  }

  // Active rooms breakdown by room_type (only rooms with data for the selected month)
  const ROOM_TYPE_ORDER = [
    'High Speed 1',
    'High Speed 2',
    'Medium Speed',
    'Low Speed 1',
    'Low Speed 2',
    'Overlay',
    'SAPS',
  ];
  const roomsByType: Record<string, number> = {};
  for (const room of data.rooms) {
    if (!room.months[selectedMonth]) continue;
    const rt = room.room_type ?? 'Other';
    roomsByType[rt] = (roomsByType[rt] ?? 0) + 1;
  }
  const roomParts: string[] = [];
  for (const rt of ROOM_TYPE_ORDER) {
    if (roomsByType[rt]) {
      roomParts.push(`${roomsByType[rt]} ${rt}`);
    }
  }
  // Catch-all for any room types not in the canonical order
  for (const rt of Object.keys(roomsByType)) {
    if (!ROOM_TYPE_ORDER.includes(rt)) {
      roomParts.push(`${roomsByType[rt]} ${rt}`);
    }
  }

  // Total line hours for the selected month
  const totalLineHours = data.rooms.reduce((sum, room) => {
    return sum + (room.months[selectedMonth]?.line_hours ?? 0);
  }, 0);

  // Packaging (PA + MO) vs Quality (QI)
  const paHc = gt?.pa_hc ?? 0;
  const moHc = gt?.mo_hc ?? 0;
  const qiHc = gt?.qi_hc ?? 0;
  const pkgHc = paHc + moHc;

  return [
    {
      icon: 'pi pi-users',
      label: 'Current Month HC',
      value: gt?.total_hc?.toFixed(1) ?? '--',
      subtitle: `PA: ${gt?.pa_hc?.toFixed(1) ?? '--'} | MO: ${gt?.mo_hc?.toFixed(1) ?? '--'} | QI: ${gt?.qi_hc?.toFixed(1) ?? '--'}`,
      accent: '#522d6e',
      accentLight: 'rgba(82, 45, 110, 0.08)',
    },
    {
      icon: 'pi pi-arrow-up',
      label: 'Peak Headcount',
      value: peakValue.toFixed(1),
      subtitle: formatMonthLabel(peakMonth),
      accent: '#d32f2f',
      accentLight: 'rgba(211, 47, 47, 0.08)',
    },
    {
      icon: 'pi pi-building',
      label: 'Active Rooms',
      value: data.summary.total_rooms_active,
      subtitle: roomParts.join(' | ') || '--',
      accent: '#34b53a',
      accentLight: 'rgba(52, 181, 58, 0.08)',
    },
    {
      icon: 'pi pi-box',
      label: 'Forecast Jobs',
      value: currentMonthJobs.toLocaleString(),
      subtitle: `12-mo total: ${totalJobs12.toLocaleString()}`,
      accent: '#f59e0b',
      accentLight: 'rgba(245, 158, 11, 0.08)',
    },
    {
      icon: 'pi pi-clock',
      label: 'Total Line-Hours',
      value: totalLineHours.toFixed(1),
      subtitle: `${formatMonthLabel(selectedMonth)} total`,
      accent: '#0891b2',
      accentLight: 'rgba(8, 145, 178, 0.08)',
    },
    {
      icon: 'pi pi-chart-pie',
      label: 'Packaging vs Quality',
      value: `${pkgHc.toFixed(1)}`,
      subtitle: `Pkg: ${pkgHc.toFixed(1)} | Qual: ${qiHc.toFixed(1)}`,
      accent: '#7c3aed',
      accentLight: 'rgba(124, 58, 237, 0.08)',
    },
  ];
};

interface CPSummaryCardsProps {
  selectedMonth: string;
}

const CPSummaryCards = observer(({ selectedMonth }: CPSummaryCardsProps) => {
  const store = useCapacityStore();
  const data = store.dashboardData;
  if (!data) return null;

  const cards = buildCards(data, selectedMonth, store.filteredMonths);

  return (
    <div className={styles.cardsGrid}>
      {cards.map((card) => (
        <Card key={card.label} {...card} />
      ))}
    </div>
  );
});

export default CPSummaryCards;
